
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { API } from '../../api';
import type { SharePointConnectionStatus } from '../../types/sharepoint';
import DosenButton from '../ui/Buttons/DosenButton';
import { Bone } from '../ui/DashboardSkeletons';
import SharePointConnectModal from './SharePointConnectModal';
import { MOBILE_SX } from '../../mobile/mobileApp';

interface Props {
  orgId: string;
  onSyncComplete: () => void;
}

function formatRelativeTime(timestamp: number): string {
  const diffMs = Date.now() - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
}

function truncateUrl(url: string, maxLen = 45): string {
  if (url.length <= maxLen) return url;
  return `${url.slice(0, maxLen)}...`;
}

function SharePointIcon({ size = 20 }: { size?: number }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}
    >
      <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="20" height="20" rx="3" fill="#0078D4" />
        <rect x="3" y="3" width="8" height="8" rx="1.5" fill="#fff" fillOpacity="0.9" />
        <rect x="9" y="9" width="8" height="8" rx="1.5" fill="#fff" fillOpacity="0.6" />
        <rect x="3" y="11" width="6" height="6" rx="1.5" fill="#fff" fillOpacity="0.45" />
      </svg>
    </Box>
  );
}

export default function ConnectedSources({ orgId, onSyncComplete }: Props) {
  const [status, setStatus] = useState<SharePointConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await API.getSharePointConnection(orgId);
      setStatus(data);
    } catch {
      // If 404 / not configured, treat as disconnected
      setStatus({
        isConnected: false,
        status: 'disconnected'
      });
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await API.triggerSharePointSync(orgId);
      await fetchStatus();
      onSyncComplete();
    } catch {
      // Sync trigger failed silently — status will reflect this on next refresh
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect SharePoint? Synced documents will remain in Knowledge.')) return;
    setDisconnecting(true);
    try {
      await API.deleteSharePointConnection(orgId);
      await fetchStatus();
    } catch {
      // ignore
    } finally {
      setDisconnecting(false);
    }
  };

  const handleConnected = async () => {
    setLoading(true);
    await fetchStatus();
    onSyncComplete();
  };

  if (loading) {
    return (
      <Bone height={72} />
    );
  }

  const isConnected = status?.isConnected === true;

  if (!isConnected) {
    return (
      <>
        <Box
          sx={{
            p: '16px 20px',
            borderRadius: '6px',
            outline: '1px solid #2A2A2A',
            outlineOffset: '-1px',
            background: '#1A1A1A',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            // A phone: the button under the text, the card's full width.
            [MOBILE_SX]: { flexDirection: 'column', alignItems: 'stretch', gap: '12px', p: '14px' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, [MOBILE_SX]: { alignItems: 'flex-start' } }}>
            <SharePointIcon size={28} />
            <Box>
              <Typography sx={{ color: '#FDFDFD', fontSize: 15, fontWeight: 600, lineHeight: '20px' }}>
                Connect SharePoint
              </Typography>
              <Typography sx={{ color: '#868686', fontSize: 13, fontWeight: 400, lineHeight: '18px', mt: 0.25 }}>
                Automatically sync documents from your SharePoint into Dosen Knowledge, with no manual uploads needed.
              </Typography>
            </Box>
          </Box>
          <DosenButton
            tone="basic"
            sizeVariant="sm"
            onClick={() => setModalOpen(true)}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0, [MOBILE_SX]: { minHeight: 40 } }}
          >
            Connect SharePoint
          </DosenButton>
        </Box>

        <SharePointConnectModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onConnected={handleConnected}
          orgId={orgId}
        />
      </>
    );
  }

  return (
    <Box
      sx={{
        p: '16px 20px',
        borderRadius: '6px',
        outline: '1px solid #2A2A2A',
        outlineOffset: '-1px',
        background: '#1A1A1A',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        [MOBILE_SX]: { p: '14px', gap: '12px' }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <SharePointIcon size={28} />
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: '#FDFDFD', fontSize: 15, fontWeight: 600, lineHeight: '20px' }}>
              SharePoint
            </Typography>
            <Box
              sx={{
                px: 1,
                py: '2px',
                background: 'rgba(76, 175, 80, 0.12)',
                borderRadius: '20px'
              }}
            >
              <Typography sx={{ color: '#4CAF50', fontSize: 11, fontWeight: 600, lineHeight: '16px' }}>
                {status?.status === 'syncing' ? 'Syncing' : 'Connected'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25, flexWrap: 'wrap' }}>
            {status?.siteUrl && (
              <Typography sx={{ color: '#868686', fontSize: 12, lineHeight: '18px' }}>
                {truncateUrl(status.siteUrl)}
              </Typography>
            )}
            {status?.lastSyncAt && (
              <Typography sx={{ color: '#868686', fontSize: 12, lineHeight: '18px' }}>
                Last synced: {formatRelativeTime(status.lastSyncAt)}
              </Typography>
            )}
            {status?.documentCount !== undefined && (
              <Typography sx={{ color: '#868686', fontSize: 12, lineHeight: '18px' }}>
                {status.documentCount} document{status.documentCount !== 1 ? 's' : ''} synced
              </Typography>
            )}
          </Box>

          {status?.errorMessage && (
            <Typography sx={{ color: '#F44336', fontSize: 12, lineHeight: '18px', mt: 0.25 }}>
              {status.errorMessage}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
        <DosenButton
          tone="basic"
          sizeVariant="sm"
          onClick={handleSync}
          loading={syncing || status?.status === 'syncing'}
          loadingText="Syncing..."
        >
          Sync Now
        </DosenButton>

        <Typography
          component="button"
          onClick={handleDisconnect}
          disabled={disconnecting}
          sx={{
            background: 'none',
            border: 'none',
            cursor: disconnecting ? 'default' : 'pointer',
            color: disconnecting ? '#565656' : '#868686',
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '18px',
            p: 0,
            '&:hover': { color: disconnecting ? '#565656' : '#F44336' },
            transition: 'color 0.15s'
          }}
        >
          {disconnecting ? 'Disconnecting...' : 'Disconnect'}
        </Typography>
      </Box>
    </Box>
  );
}
